<template>
  <div class="border-main mt-5 rounded-lg px-3 lg:px-24 py-3 w-full">
    <div class="dialog-header">
      <div class="">
        <div class="flex w-full items-center">
          <div
            class="text-xl lg:text-2xl w-full text-fg-default font-semibold flex justify-center my-4"
          >
            Wire Instructions
          </div>
        </div>
      </div>
      <div
        class="flex items-center justify-center mb-8 bg-muted rounded-lg shadow-sm w-80 mx-auto"
      >
        <div
          v-if="!isEURorGBPTransfer"
          class="flex items-center justify-between gap-x-1.5 lg:gap-x-1 font-light text-xs lg:text-xs lg:text-sm bg py-1.5 rounded-lg"
        >
          <div
            class="cursor-pointer px-6 py-2.5 transition-all duration-150 ease-linear"
            :class="activeClass('Local Transfer')"
            @click="changeTransferInstructionType('Local Transfer')"
          >
            {{ getCountryName(currentTransaction.depositedAmount.currency) }}
            Only
          </div>
          <div
            class="cursor-pointer px-6 py-2.5 transition-all duration-150 ease-linear"
            :class="activeClass('International Transfer')"
            @click="changeTransferInstructionType('International Transfer')"
          >
            International
          </div>
        </div>
      </div>
      <div
        class="text-fg-default h-11 my-2 flex justify-center font-normal text-xs"
      >
        <div
          v-if="isTreasury"
          class="mx-2 bg-root-yellow text-13px border border-yellow-10 text-root-yellow rounded-md px-4 items-center flex"
          style="word-break: normal"
        >
          <warning-icon class="mr-4 -mt-2" />
          <span style="word-break: normal">{{ warningText }}</span>
        </div>
        <div
          style="word-break: normal"
          v-if="isTerminal"
          class="mx-6 text-xs text-center"
        >
          You are to send exactly
          <span class="text-primary font-semibold text-base">
            {{
              parseMoneyAll(
                transferDetails.debitAmount,
                currentTransaction.depositedAmount.currency
              )
            }}</span
          >. <span style="word-break: normal">{{ warningText }}</span>
        </div>
      </div>
    </div>
    <div class="text-xs lg:text-sm text-fg-default mt-7">
      <div class="border-bottom-main pl-5 pr-8">
        <copy-all :details="wireTransferDetails" />
      </div>
      <div
        v-if="wireTransferDetails.reference"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Reference:</div>
        <div class="col-span-3 font-semibold mx-2 truncate w-44">
          {{ wireTransferDetails.reference }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.reference"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.accountName"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Account Name:</div>
        <div class="col-span-3 font-semibold mx-2">
          {{ wireTransferDetails.accountName }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.accountName"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.accountNumber && !isEURorGBPTransfer"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Account Number:</div>
        <div class="col-span-3 font-semibold mx-2 break-all">
          {{ wireTransferDetails.accountNumber }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.accountNumber"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.iban"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">IBAN</div>
        <div class="col-span-3 font-semibold mx-2 break-all">
          {{ wireTransferDetails.iban }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.iban"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.bankPhone"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Bank Phone:</div>
        <div class="col-span-3 font-semibold mx-2">
          {{ wireTransferDetails.bankPhone }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.bankPhone"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.bankAddress"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Bank Address:</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.bankAddress }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.bankAddress"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.beneficiaryAddress"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Beneficiary Address:</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.beneficiaryAddress }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.beneficiaryAddress"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.creditTo"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Credit To:</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.creditTo }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.creditTo"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.depositoryBankName"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 mr-8 font-light">Depository Bank Name:</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.depositoryBankName }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.depositoryBankName"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.routingNumber"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Routing-Number:</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.routingNumber }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.routingNumber"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.swiftCode"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Swift-Code</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.swiftCode }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.swiftCode"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
      <div
        v-if="wireTransferDetails.sortCode"
        class="my-4 py-2 grid grid-cols-6 px-4 border-bottom-main pb-3"
      >
        <div class="col-span-2 font-light">Sort-Code</div>
        <div class="col-span-3 mx-2 font-semibold">
          {{ wireTransferDetails.sortCode }}
        </div>
        <div
          class="justify-self-center cursor-pointer"
          v-clipboard:copy="wireTransferDetails.sortCode"
          v-clipboard:success="onCopy"
          v-clipboard:error="onError"
        >
          <copy-icon />
        </div>
      </div>
    </div>
    <div class="flex items-center justify-center gap-2 md:gap-3 mt-12">
      <div
        class="text-xs lg:text-sm font-light hover:opacity-80 bg-subtle border-main rounded-lg shadow-sm flex justify-center py-3.5 lg:py-4 cursor-pointer text-primary w-full"
        @click="goToEnterAmount"
      >
        Transfer Later
      </div>
      <div
        @click="completedDeposit"
        class="bg-primary cursor-pointer font-light hover:opacity-80 text-white py-3.5 lg:py-4 rounded-lg shadow-sm w-full"
      >
        <div class="text-xs lg:text-sm flex justify-center">Done</div>
      </div>
    </div>
    <div
      class="flex items-center justify-center text-center text-xs lg:text-sm text-primary cursor-pointer font-semibold my-8"
    >
      <p
        class="flex items-center justify-center text-center gap-x-3"
        @click="generatePdf"
      >
        <download-icon />
        <span>Download PDF</span>
      </p>
    </div>
    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <deposit-details
          :transferType="transferType"
          :terminalWireTransferDetails="wireTransferDetails"
        />
      </section>
    </vuehtml2pdf>
  </div>
</template>

<script>
import country from '@/mixins/getCountryDetails.js'
import {
  ConvertValueToXDP,
  GetCountryName,
  ParseMoney,
  ParseMoneyAll
} from '@/utils/helpers'
export default {
  mixins: [country],
  props: {
    transferType: {
      type: String,
      default: ''
    },
    useType: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      transferInstructionType: 'Local Transfer',
      wireTransferDetails: null,
      warningText:
        'Please ensure you use the correct reference when making a deposit'
    }
  },
  methods: {
    getCountryName (currency) {
      return GetCountryName(currency)
    },
    goToEnterAmount () {
      this.$emit('goToEnterAmount')
    },
    activeClass (type) {
      return this.transferInstructionType === type ? 'active-class' : ''
    },
    setActiveTab (type) {
      this.transferInstructionType = type
    },
    generatePdf () {
      this.$refs.DownloadComp.generatePdf()
    },
    completedDeposit () {
      this.$emit('closeDialog')
    },
    parseMoney (val) {
      return ParseMoney(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    convertValueTo2DP (val) {
      return ConvertValueToXDP(val, 2)
    },
    changeTransferInstructionType (type) {
      this.transferInstructionType = type
      this.wireTransferDetails =
        type === 'Local Transfer'
          ? this.wireInstruction
          : this.wireInstructionIntl
      this.$store.dispatch('transactions/wireTransferType', type)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    handleClose () {
      this.$emit('closeDialog')
    }
  },
  components: {
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    WarningIcon: () => import('@/assets/icons/WarningIcon.vue'),
    DepositDetails: () => import('@/components/deposit/DepositDetails.vue'),
    DownloadIcon: () => import('@/assets/v2/icons/DownloadIcon.vue')
  },
  computed: {
    isTreasury () {
      return this.transferType === 'treasury'
    },
    isEURorGBPTransfer () {
      return (
        this.currentTransaction.depositedAmount.currency ===
          this.$CURRENCY.GBP ||
        this.currentTransaction.depositedAmount.currency === this.$CURRENCY.EUR
      )
    },
    isTerminal () {
      return this.transferType === 'terminal'
    },
    fileName () {
      return `${process.env.VUE_APP_TENANT_TITLE} Deposit Details`
    },
    routeName () {
      return this.$route.name
    },
    isExternalApi () {
      return this.useType === 'externalApi'
    },
    currentTransaction () {
      return this.$store.state.transactions.currentTransaction
    },
    transactionDetails () {
      return this.$store.state.transactions.transactionDetails
    },
    transactionType () {
      return this.currentTransaction.transactionType
    },
    wireInstructionIntl () {
      return this.transactionType === this.$transactionType.DEPOSIT
        ? this.transactionDetails.transferInstruction.wireInstructionIntl
        : this.transactionDetails.transferInstruction.account_details
            .wireInstructionIntl
    },
    wireInstruction () {
      return this.transactionType === this.$transactionType.DEPOSIT
        ? this.transactionDetails.transferInstruction.wireInstruction
        : this.transactionDetails.transferInstruction.account_details
            .wireInstruction
    },
    transferDetails () {
      return (
        this.$store.state.terminal.transferTerminalDetails.transferDetails || {}
      )
    },
    transferInfo () {
      return (
        this.$store.state.terminal.transferInfo.transfer.depositedAmount || {}
      )
    }
  },
  mounted () {
    this.wireTransferDetails = this.wireInstruction
    this.$store.dispatch(
      'transactions/wireTransferType',
      this.transferInstructionType
    )
  }
}
</script>

<style scope lang="scss">
>>> .el-button.is-disabled {
  background-color: #f4a177;
  color: white;
}
>>> .el-icon-close:before {
  padding: 25px;
  opacity: 0.9;
  color: #c1c4ca;
}
>>> .el-dialog__headerbtn :hover {
  color: #c1c4ca !important ;
}
>>> .el-dialog {
  z-index: 50;
}
.active-class {
  background: #ffffff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  font-weight: 600;
  @apply text-primary;
}
</style>
